import { IBetaEstimateResult } from '@models/beta/beta-estimate';
import { IBetaTimeSeries } from '@models/beta/betaTimeSeries';
import { IMarketRate } from '@models/beta/marketrate';
import { IZoomLevel } from '@models/beta/zoomlevel';
import { clone } from '@shared/arrays';
import { filterTimeSeriesByMonthYear, getDateString, getLastDayOfNextMonth } from '@shared/dates';
import { IExportChartOption } from '../export.config';

export const createScatterPlotJSON = (
  marketRate: IMarketRate,
  betaTimeSeries: IBetaTimeSeries,
  estimate: IBetaEstimateResult,
  exportChartOption: IExportChartOption,
  zoomLevel?: IZoomLevel
) => {
  const chartObj = {
    exporting: {
      sourceWidth: exportChartOption.width,
      sourceHeight: exportChartOption.height,
      allowHTML: true
    },
    chart: {
      id: `scatterGrap_${estimate.Name}`,
      type: 'scatter'
    },
    title: {
      text: ''
    },
    yAxis: [
      {
        gridLineWidth: 1,
        title: {
          text: 'Coupon (%)'
        }
      }
    ],
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      alignColumns: true,
      itemWidth: 160
    },
    plotOptions: {
      series: {
        animation: {
          duration: 3000,
          easing: 'easeOutBounce'
        }
      }
    },
    xAxis: {
      title: {
        text: 'Market Rate (%)'
      }
    },
    series: [],
    credits: {
      enabled: false
    }
  };

  // Adding data

  // console.log('Values Entering the Export\n',{"Market Rate": marketRate},  {"Product Rate": betaTimeSeries});

  const betaTimeSeriesFiltered: IBetaTimeSeries = clone(betaTimeSeries);
  const marketRateFiltered: IMarketRate = clone(marketRate);

  /**
   * Filtering date range zoom
   */
  if (zoomLevel) {
    const startDate = new Date(zoomLevel.min);
    const endDate = new Date(zoomLevel.max);

    const [minIndexBeta, maxIndexBeta] = filterTimeSeriesByMonthYear(startDate, endDate, betaTimeSeries.PeriodEndDateSeries);

    if (minIndexBeta >= 0 && maxIndexBeta >= 0) {
      // Splicing all the data to create our filtered object
      betaTimeSeriesFiltered.ProductRateSeries = betaTimeSeriesFiltered.ProductRateSeries.splice(
        minIndexBeta,
        maxIndexBeta - minIndexBeta + 1
      );

      betaTimeSeriesFiltered.PeriodEndDateSeries = betaTimeSeriesFiltered.PeriodEndDateSeries.splice(
        minIndexBeta,
        maxIndexBeta - minIndexBeta + 1
      );
    }

    // Adjusting for the lag on the market rate
    if (estimate.Lag) {
      marketRateFiltered.TimeSeries.map(val => {
        val.PeriodEndDate = getDateString(getLastDayOfNextMonth(new Date(val.PeriodEndDate), +estimate.Lag));
      });
    }

    // tslint:disable-next-line:prefer-const
    let [minIndexMarketRate, maxIndexMarketRate] = filterTimeSeriesByMonthYear(
      startDate,
      endDate,
      marketRateFiltered.TimeSeries.map(bal => bal.PeriodEndDate)
    );

    // ! Fix 8267
    if (maxIndexMarketRate === -1) {
      maxIndexMarketRate = marketRate.TimeSeries.length - 1;
    }

    if (minIndexMarketRate >= 0 && maxIndexMarketRate >= 0) {
      marketRateFiltered.TimeSeries = marketRateFiltered.TimeSeries.slice(minIndexMarketRate, maxIndexMarketRate + 1);
    } else {
      throw Error(`Unable to find position in market rate time series at startDate: ${startDate.toJSON()} End date: ${endDate.toJSON()} `);
    }
  }

  if (marketRateFiltered.TimeSeries.length < betaTimeSeriesFiltered.ProductRateSeries.length) {
    console.warn(`Some issue with market rate ${marketRateFiltered.MarketRateName}`, {
      'beta time series ': betaTimeSeries,
      'market rate': marketRate
    });

    // console.log(
    //   // tslint:disable-next-line:max-line-length
    //   `Market Rate Length ${marketRateFiltered.TimeSeries.length}\nBeta Time Series Length ${betaTimeSeriesFiltered.ProductRateSeries.length}`
    // );

    return {};
  }

  // Creating the data object for the scatter plot data
  // Logging min and max as well
  let min = Infinity;
  let max = -Infinity;

  const pointData = [];
  betaTimeSeriesFiltered.ProductRateSeries.forEach((rate, index) => {
    if (marketRateFiltered.TimeSeries[index].Rate * 100 > max) {
      max = marketRateFiltered.TimeSeries[index].Rate * 100;
    }

    if (marketRateFiltered.TimeSeries[index].Rate * 100 < min) {
      min = marketRateFiltered.TimeSeries[index].Rate * 100;
    }

    const point = [marketRateFiltered.TimeSeries[index].Rate * 100, rate * 100];

    pointData.push(point);
  });

  const pointOptions = {
    data: pointData,
    name: 'Market/Product Rate',
    color: '#7FB3D5',
    animation: {
      duration: 800,
      easing: 'easeOutBounce'
    }
  };

  chartObj.series.push(pointOptions);

  const data = [];
  if (estimate.Id) {
    const x1 = min;
    const x2 = max;
    const y1 = estimate.Slope * x1 + estimate.Intercept * 100;
    const y2 = estimate.Slope * x2 + estimate.Intercept * 100;

    const firstPoint = [x1, y1];
    const lastPoint = [x2, y2];

    data.push(firstPoint, lastPoint);
  }
  const predictedOptions = {
    data,
    type: 'line',
    name: ' Fitted Values',
    color: '#FF5500',
    marker: {
      enabled: false
    },
    states: {
      hover: {
        lineWidth: 0
      }
    },
    animation: {
      duration: 800,
      easing: 'easeOutBounce'
    },
    enableMouseTracking: false
  };

  // console.log(
  //   'export data Data\n',
  //   { 'filtered market rate series': marketRateFiltered },
  //   { 'filtered product rate': betaTimeSeriesFiltered },
  //   { 'points filtered': data }
  // );

  chartObj.series.push(predictedOptions);
  return chartObj;
};
